<template>
    <div class="phone-verification-container">
        <div class="divider"></div>

        <div class="verification-container">
            <div class="verification-header">
                <p id="phone-otp-label" class="verification-title">Verification Code</p>
            </div>
            <div class="otp-container" role="group" aria-labelledby="phone-otp-label">
                <!-- input otp -->
                <prime-input-otp v-model="otp" :length="6" size="xs" @keydown.enter="onEnter" @paste="onPaste">
                    <template #default="{ attrs, events, index }">
                        <input
                            type="text"
                            v-bind="attrs"
                            v-on="events"
                            :aria-label="`Verification code digit ${index} of 6`"
                            inputmode="numeric"
                            autocomplete="one-time-code"
                            class="custom-otp-input"
                        />
                        <span v-if="index === 3" class="otp-dash" aria-hidden="true">-</span>
                    </template>
                </prime-input-otp>
            </div>
            <div class="resend-container">
                <p class="resend-text">
                    Didn't get a code?
                    <button
                        type="button"
                        :disabled="isSmsResent || isResending"
                        :class="{
                            'resend-link': true,
                            'resend-link-active': !isSmsResent && !isResending,
                            'resend-link-disabled': isSmsResent || isResending,
                        }"
                        @click="handleResend"
                    >
                        Click to resend</button
                    >.
                </p>
            </div>
            <div class="divider my-3" v-if="isSmsResent || isInvalidCode"></div>
            <!-- Mounted throughout and sr-only while idle: a polite region that appears already
                 holding its text is not reliably announced. -->
            <div :class="isSmsResent ? 'status-container' : 'sr-only'" role="status">
                <template v-if="isSmsResent">
                    <CheckCircleIcon class="status-icon success" aria-hidden="true" />
                    <p class="status-text success">Verification code re-sent!</p>
                </template>
            </div>
            <!-- Mounted throughout for the same reason, as an assertive region. Only a code the API
                 refused as wrong reads here; every other refusal is the person store's toast. -->
            <div :class="isInvalidCode ? 'status-container' : 'sr-only'" role="alert">
                <template v-if="isInvalidCode">
                    <ExclamationCircleIcon class="status-icon error" aria-hidden="true" />
                    <p class="status-text error">That code did not work. Check it and try again.</p>
                </template>
            </div>
        </div>
    </div>

    <div class="footer-container">
        <div class="button-container">
            <PrimeButton label="Cancel" @click="closeDialog" variant="secondary" class="p-btn-root_secondary"/>

            <PrimeButton @click="verifyPhone" :loading="isVerifyingOtp" :disabled="otp.length < 6">
                     {{ isVerifyingOtp ? 'Verifying...' : 'Verify & Add' }}
            </PrimeButton>
        </div>
    </div>
</template>
<script setup lang="ts">
import { CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/vue/24/solid';
import { getDialogRef } from '~/composables/dynamic-dialog';
import { useVaultStore } from '~/stores/person/vault/vault';
import { ModalNames } from '~/types/modals';
import { ApiError } from '~/utils/errors';

const { handleClearToast } = useGlobalToast();
const isSmsResent = ref(false);
const isResending = ref(false);
const isInvalidCode = ref(false);
const otp = ref('');

//emit
const emit = defineEmits(['closeDialog']);

//inject dialogRef
const dialogRef =   getDialogRef(ModalNames.PHONE_VERIFICATION);

//close dialog
const closeDialog = () => {
    emit('closeDialog');
};

const personStore = usePersonStore();
const vaultStore = useVaultStore();
const isVerifyingOtp = ref(false);

/**
 * Verify the code and add the number. `verifyPhoneOtp` toasts every failure in the words
 * `memberFacingMessage` gives it; beside the code, only the API's `invalid_code` says the code
 * itself was wrong. Any other refusal (`too_many_attempts`, which needs a new code, a number
 * already on the account) is not about what was typed, so it is the toast alone.
 */
const verifyPhone = async () => {
    // Enter and a paste both submit, so a second press while the first is out would spend
    // another of the number's attempts on the same code.
    if (isVerifyingOtp.value) return;

    try {
        handleClearToast();
        isInvalidCode.value = false;
        isVerifyingOtp.value = true;
        await personStore.verifyPhoneOtp({
            country_code: dialogRef.value.data.country_code.replace('+', ''),
            phone_number: dialogRef.value.data.phone_number,
            code: otp.value,
        });
        
        // Confirming a number that came from an exposure has to close that
        // exposure too. Shared with the unverified-add path in AddPhoneModal,
        // which confirms the same value by a different route.
        await vaultStore.resolveSourceExposure({
            fromExposure: dialogRef.value.data.fromExposure,
            exposureData: dialogRef.value.data.exposureData,
            fallbackValue: dialogRef.value.data.phone_number,
        });
        
        closeDialog();
    } catch (error) {
        isInvalidCode.value = error instanceof ApiError && error.reason === 'invalid_code';
    } finally {
        isVerifyingOtp.value = false;
    }
};

const onEnter = () => {
    verifyPhone();
};

const onPaste = () => {
    setTimeout(() => {
        verifyPhone();
    }, 0);
};

const dialogRefAddPhone = getDialogRef(ModalNames.ADD_PHONE);

const handleResend = async () => {
    if (isSmsResent.value || isResending.value) return;

    isResending.value = true;
    handleClearToast();
    otp.value = '';
    isInvalidCode.value = false;

    const selectedMethodValue = dialogRefAddPhone.value.data.method === 'sms' ? true : false;

    const requestData = {
        phone_number: dialogRefAddPhone?.value.data.phone_number as string,
        country_code: dialogRefAddPhone?.value.data.country_code as string,
        method: dialogRefAddPhone?.value.data.method || 'sms',
        fromExposure: dialogRefAddPhone?.value.data?.fromExposure || false, // Pass through the fromExposure flag
        exposureData: dialogRefAddPhone?.value.data?.exposureData || false, // Pass through the exposure data for suppression
    };

    try {
        await personStore.sendPhoneVerificationCode(
            selectedMethodValue,
            requestData,
            true
        );
        isSmsResent.value = true;

        setTimeout(() => {
            isSmsResent.value = false;
        }, 30000);
    } finally {
        isResending.value = false;
    }
};
</script>
<style lang="scss" scoped>
.phone-verification-container {
    @apply flex flex-col items-start gap-1.5 pt-4 self-stretch;
}

.divider {
    @apply h-px self-stretch bg-zinc-300;
}

.resend-container {
    @apply flex items-start;
}

.resend-text {
    @apply text-zinc-500 text-sm font-normal leading-5;
}

// Strips the UA button chrome so the real control renders exactly like the inline text it replaces.
.resend-link {
    @apply underline cursor-pointer bg-transparent border-0 p-0 text-left align-baseline;
    font: inherit;
}

.resend-link-active {
    @apply text-blue-600;
}

.resend-link-disabled {
    @apply text-gray-600;
}

.verification-container {
    @apply w-full;
}

.verification-header {
    @apply flex flex-col items-start gap-1.5 self-stretch pt-3;
}

.verification-title {
    @apply text-gray-700 text-sm font-medium leading-5;
}

.otp-container {
    @apply flex items-start sm:gap-2 gap-px h-16 py-1.5;
}

.status-container {
    @apply flex items-center gap-2 self-stretch;
}

.status-icon {
    @apply w-5 h-5;
}

.status-icon.success {
    @apply text-green-500;
}

.status-icon.error {
    @apply text-red-500;
}

.status-text {
    @apply text-sm font-normal leading-5;
}

.status-text.success {
    @apply text-gray-600;
}

.status-text.error {
    @apply text-red-500;
}

//footer
.footer-container {
    @apply flex flex-col justify-center items-end self-stretch pt-8;
}

.button-container {
    @apply flex justify-end items-center gap-3 flex-1 pb-6;
}
</style>
